import { Alert, AlertTitle, Box, Chip, Collapse, Link, Typography } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"

import { GoodsReceiptPalletDestinationOverrideDialog } from "../../WarehouseReceiving/components"
import type { DestinationOverride } from "../../WarehouseReceiving/components"
import { useScrollIntoView } from "../hooks/useScrollIntoView"

import { ScannedGoodsReceiptPalletCardDetails } from "./ScannedGoodsReceiptPalletCardDetails"

import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"

const blinkAnimation = {
  "@keyframes blink": {
    "0%, 100%": { backgroundColor: "#FFFFFF" },
    "50%": { backgroundColor: "#77A3CA" },
  },
}

interface ScannedGoodsReceiptPalletCardProps {
  pallet: GoodsReceiptPallet
  repeatedScan?: boolean
  scanCount?: number
  pallets: GoodsReceiptPallet[]
  destinationOverrides: Map<string, DestinationOverride>
  onDestinationOverride?: (pallet: GoodsReceiptPallet, override: DestinationOverride) => Promise<void>
  onQrPrint: () => void
}

export const ScannedGoodsReceiptPalletCard: FC<ScannedGoodsReceiptPalletCardProps> = ({
  pallet,
  repeatedScan = false,
  scanCount = 1,
  pallets,
  destinationOverrides,
  onDestinationOverride,
  onQrPrint,
}) => {
  const shouldAnimate = repeatedScan && scanCount > 1
  const [isExpanded, setIsExpanded] = useState(false)
  const [isOverrideDestinationDialogOpen, setIsOverrideDestinationDialogOpen] = useState(false)

  const { ref: cardRef } = useScrollIntoView({
    trigger: scanCount,
    enabled: scanCount > 1,
  })

  const warehouse = pallet.goodsReceiptPalletRow?.goodsReceipt?.warehouseArea

  const productInstructions = pallet.goodsReceiptPalletRow?.product?.instructions || []

  const handleToggleExpanded = () => {
    setIsExpanded((prev) => !prev)
  }

  const handleOpenOverrideDestinationDialog = () => {
    if (!warehouse) {
      return
    }

    setIsOverrideDestinationDialogOpen(true)
  }

  const handleCloseOverrideDestinationDialog = () => {
    setIsOverrideDestinationDialogOpen(false)
  }

  const handleConfirmOverrideDestination = async (override: DestinationOverride) => {
    await onDestinationOverride?.(pallet, override)
    setIsOverrideDestinationDialogOpen(false)
  }

  return (
    <Box
      ref={cardRef}
      data-testid={`product-card`}
      key={shouldAnimate ? `blink-${scanCount}` : undefined}
      sx={{
        ...blinkAnimation,
        border: repeatedScan ? 5 : 1,
        borderColor: repeatedScan ? "primary.main" : "divider",
        borderRadius: 1,
        p: 2,
        bgcolor: "#FFFFFF",
        boxSizing: "border-box",
        animation: shouldAnimate ? "blink 0.5s ease-in-out 2" : "none",
      }}
    >
      <Box className='mb-4 flex items-center gap-2'>
        <Typography variant='body1' fontWeight={500}>
          {pallet.goodsReceiptPalletRow?.product?.companyProductDescription}
        </Typography>
        <Chip label={`Ref ID: ${pallet.referenceId}`} size='small' className='bg-yellow-300 text-tertiary-base' />
      </Box>

      {pallet.destinationWarehouseShelving ? (
        <Box className='mb-4 grid grid-cols-3 gap-4 text-center'>
          <Box>
            <Typography variant='h2' fontWeight={700} sx={{ fontSize: { xs: "3rem", sm: "4rem" } }}>
              {pallet.destinationWarehouseShelving.aisleCode}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Aisle
            </Typography>
          </Box>

          <Box>
            <Typography variant='h2' fontWeight={700} sx={{ fontSize: { xs: "3rem", sm: "4rem" } }}>
              {pallet.destinationWarehouseShelving.sectionCode}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Shelf
            </Typography>
          </Box>

          <Box>
            <Typography variant='h2' fontWeight={700} sx={{ fontSize: { xs: "3rem", sm: "4rem" } }}>
              {pallet.destinationWarehouseShelving.binNumber}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Bin
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box>
          <Typography variant='h2' fontWeight={700} sx={{ fontSize: { xs: "3rem", sm: "4rem" } }}>
            None assigned
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Storage location
          </Typography>
        </Box>
      )}

      {!isExpanded && (
        <Box className='mt-2 flex items-center gap-2'>
          <Link
            href='#'
            onClick={(e) => {
              e.preventDefault()
              handleToggleExpanded()
            }}
            className='flex items-center gap-1 font-medium no-underline'
            data-testid='toggle-details-link'
          >
            More Details
          </Link>
        </Box>
      )}

      <Collapse in={isExpanded}>
        <ScannedGoodsReceiptPalletCardDetails
          pallet={pallet}
          onToggleExpanded={handleToggleExpanded}
          onDestinationOverride={onDestinationOverride ? handleOpenOverrideDestinationDialog : undefined}
          onQrPrint={onQrPrint}
        />
      </Collapse>

      {productInstructions?.length > 0 && (
        <Alert severity='warning' className='mt-4' data-testid='special-instructions-alert'>
          <AlertTitle>Special Instructions</AlertTitle>
          {productInstructions.map((instruction) => (
            <Typography key={instruction.instructionId} variant='body2'>
              {instruction.description}
            </Typography>
          ))}
        </Alert>
      )}

      {warehouse && isOverrideDestinationDialogOpen && (
        <GoodsReceiptPalletDestinationOverrideDialog
          pallet={pallet}
          pallets={pallets}
          warehouse={warehouse}
          destinationOverrides={destinationOverrides}
          onClose={handleCloseOverrideDestinationDialog}
          onSubmit={handleConfirmOverrideDestination}
        />
      )}
    </Box>
  )
}
